// mu — waitForEvents: the one owned-event wait.
//
// Before this there were two poll loops (one for tasks, one for agents) and a
// shell loop above them; none could see a delegate's report or end on any
// event but its own. This is the single loop.
//
// One iteration: beforePoll, a bounded sync ingest, the DB reads (tasks,
// delegates, the caller's registration), ONE batched agent read, apply the
// CLOSE blocks of settled pi runs, then pick. Then it races an agent watch
// settling (ctl `wait`, never awaited serially), a poll sleep, the caller's
// abort signal and the deadline. Every await inside an iteration is raced
// with abort and deadline too, so no read can hold the wait past either.
// No database transaction spans an await.
//
// What a wait watches:
//   targets  the explicit refs, a wave's delegate calls (this caller's) and
//            the open transitive blockers of `blockersOf` (re-read each
//            iteration, so graph edits count).
//   owned    what the caller's registration owes it, plus its in-flight
//            delegate calls (registered the first time a wait sees them).
//            Owned refs are observed even while explicit targets are waited
//            on; another caller's work is never seen.
//
// first vs all. `first` returns the best ready event over targets and owned
// refs. `all` returns once every target is done (no targets: every owned
// ref); an urgent event (an owned ref that died, an agent blocked on a
// dialog, a stall, an owned delegate report) ends it early without counting
// toward "all". Order: never-returned events first, then dead, input, stall,
// delegate, then closed/settled; targets before owned refs. One fact is one
// event: a settled run and the close its CLOSE block made, or a dead task and
// its dead owner (`sameDeath`), are delivered (and consumed) together.
//
// Delivery is durable, never in-memory. Every event has a cursor key
// (src/wait-cursor.ts): a close or settle under the ref's own key, an urgent
// observation under `input|`, `dead|` or `stall|` + the ref key with a token
// that names ITS state (the dialog's `since`, the owner's registry id, the
// stall token), a blockersOf blocker seen open under `open|` + key. An event
// is consumed only when it is the one returned, in ONE short transaction
// guarded by the registration's generation (endWait) that first re-reads the
// cursors: an event another process consumed meanwhile is skipped and the
// wait selects again. A sibling that `first`
// did not return stays owed (a timeout or interrupt delivers nothing), a
// returned event is never delivered again, and an unchanged urgent
// observation is news once per state change: it never outranks a
// never-returned event, so it cannot starve a delegate report. Explicit refs
// ignore the cursor of their completion (waiting on a closed task returns at
// once); owned refs obey it.
//
// Owed vs done (src/wait-owed.ts). An owned agent ref is owed only while the agent owns open work or a
// run it was sent is unsettled. Each tick keeps a DONE mark per owned agent true to what it read; a
// registered agent whose row is gone and which is marked done is dropped from the registration with
// no event. `dead` is a pane lost while work or a run is owed (or never read).
//
// The fence. An agent ref the caller registered, explicit or owned, counts
// its runs from the stored UNCONSUMED baseline, maxed with the consumed
// cursor of the same pi process; with neither it counts from the run count
// probed now, stores that as the fence and says `unknownBaseline`: a
// historical run is never mistaken for completion.
//
// CLOSE. A settled pi run's CLOSE block is applied once (applySettledClose)
// before the snapshot returns. The close-block refuses delegate and human
// tasks, so a delegate answer never closes its own task ahead of
// finishDelegate's recorder.
//
// Stalls. The engine detects them (an IN_PROGRESS work task whose owner needs
// input or has a broken control socket, `stuckAfterMs`); `handleStall` (raced,
// once per stallToken) owns the policy and effects.
//
// Not here: the CLI and signal wiring, arming from spawn/send/claim
// (v_wait_cli).

import { agentKey } from "./agent-state.js";
import { AgentNotFoundError, AgentRestartedError } from "./agents/errors.js";
import { closeNotices } from "./close-notice.js";
import { type Db, tryResolveWorkstreamId } from "./db.js";
import {
  DelegateNotFoundError,
  type DelegateRow,
  getDelegateCall,
  listDelegatesOf,
  tallyCalls,
  type VerdictTally,
} from "./delegates.js";
import { openHumanNeeds } from "./human-needed.js";
import { emitEvent } from "./logs.js";
import { taskRevision } from "./revision.js";
import { ambientFlush, ambientIngest } from "./sync.js";
import { taskIdFor } from "./tasks/core.js";
import { TaskNotFoundError } from "./tasks/errors.js";
import { getTask, getTaskOwner } from "./tasks/queries.js";
import { DEFAULT_SUBSTATE, type TaskStatus } from "./tasks/status.js";
import {
  type CursorToken,
  closedToken,
  isConsumed,
  logStallOnce,
  readCursors,
  recordCursors,
} from "./wait-cursor.js";
import {
  agentExists,
  agentRowId,
  closeSettledRun,
  openBlockers,
  opsHead,
  reapFlip,
  taskOwnerRowId,
} from "./wait-db.js";
import {
  agentSnapshot,
  delegateItem,
  exitFor,
  failedGate,
  type Item,
  inputToken,
  isCompletion,
  refKey,
  registered,
  type StallState,
  sameDeath,
  snapshotsOf,
  urgency,
} from "./wait-item.js";
import { type AgentTrack, rearmLatch, rewatchOnPoll, supersededBy } from "./wait-latch.js";
import { agentOwed, hasDoneMark, setAgentDone } from "./wait-owed.js";
import { compileTextMatcher, type TextMatcher } from "./wait-regex.js";
import {
  beginWait,
  checkCaller,
  deadRefs,
  dropOwnedRefs,
  endWait,
  fenceAgentRef,
  type RefBaseline,
  type RegisteredRef,
  readWaitRegistration,
  registerOwnedWait,
} from "./wait-registration.js";
import {
  type AgentReading,
  type CloseOutcome,
  DEFAULT_CTL_STUCK_AFTER_MS,
  DEFAULT_STUCK_AFTER_MS,
  type StallActionResult,
  WAIT_HEARTBEAT_CAP_MS,
  type WaitEventKind,
  WaitNothingError,
  type WaitOptions,
  type WaitRef,
  WaitRefError,
  type WaitResult,
  type WaitSnapshot,
  type WaitStallContext,
  WaveNotFoundError,
} from "./wait-types.js";

let sleepSeam: ((ms: number) => Promise<void>) | undefined;
/** Test seam: replace the poll sleep of every wait (undefined restores the abortable timer); returns the previous one. */
export function setWaitSleepForTests(
  impl: ((ms: number) => Promise<void>) | undefined,
): ((ms: number) => Promise<void>) | undefined {
  const previous = sleepSeam;
  sleepSeam = impl;
  return previous;
}

/** Wait for the next owned event (or all targets). See the file comment. */
export async function waitForEvents(
  db: Db,
  input: readonly WaitRef[],
  opts: WaitOptions,
): Promise<WaitResult> {
  const callers = [...new Set(opts.callers ?? [])];
  for (const c of callers) checkCaller(c);
  const all = opts.mode === "all";
  const ownedScope = opts.eventScope !== "refs";
  const stuckExplicit = opts.stuckAfterMs;
  const stuckEnabled =
    stuckExplicit !== 0 && opts.status !== "OPEN" && opts.status !== "IN_PROGRESS";
  const stuckAfter = (viaCtl: boolean): number =>
    stuckExplicit ?? (viaCtl ? DEFAULT_CTL_STUCK_AFTER_MS : DEFAULT_STUCK_AFTER_MS);
  // A wait that owns events (a caller set, or any owned-scope wait: a plain shell or CI
  // naming refs) sees an owner die; the refs-scope adapters never did.
  const watchDeaths = callers.length > 0 || ownedScope;
  const scope = opts.scope;
  const inScope = (workstream: string): boolean => scope === undefined || workstream === scope;
  /** A delegate call is in scope by its own workstream or the workstream of the task it records on. */
  const callInScope = (c: DelegateRow): boolean =>
    scope === undefined || c.workstream === scope || c.record?.workstream === scope;
  // Owners of watched tasks are read for death and for stalls.
  const observeOwners = (watchDeaths || stuckEnabled) && opts.readAgents !== undefined;
  /** The callers consume what they are handed (cursors, registration) only for owned-scope waits. */
  const consumes = ownedScope && callers.length > 0;
  /** An owned-scope wait owns what its callers spawned: the provenance discovery. */
  const target: TaskStatus = opts.status ?? "CLOSED";
  const discover = consumes && target === "CLOSED";
  const matcher: TextMatcher | undefined =
    typeof opts.until === "object" ? compileTextMatcher(opts.until.text) : undefined;
  const applyClose = opts.applyClose !== false;
  const pollMs = opts.pollMs ?? 1000;
  const requested = opts.timeoutMs ?? 0;
  const timeoutMs = opts.uncapped
    ? requested
    : requested === 0
      ? WAIT_HEARTBEAT_CAP_MS
      : Math.min(requested, WAIT_HEARTBEAT_CAP_MS);
  const deadline = timeoutMs > 0 ? Date.now() + timeoutMs : Number.POSITIVE_INFINITY;
  // A reaper flip after this ops seq is news to this wait even if no tick of it saw the owner (a
  // death between two waits, or across a resume): a resume replays the stored anchor.
  const sinceSeq = opts.sinceSeq ?? opsHead(db);

  // ─── resolve every ref upfront: typed, exact, nothing ambiguous ───
  for (const ref of input) {
    if (ref.kind === "task") {
      if (getTask(db, ref.id, ref.workstream) === undefined) {
        throw new TaskNotFoundError(`${ref.workstream}/${ref.id}`);
      }
    } else if (ref.kind === "agent") {
      if (!agentExists(db, ref)) throw new AgentNotFoundError(ref.id);
    } else {
      const call = getDelegateCall(db, ref.id);
      if (call === undefined || call.workstream !== ref.workstream) {
        throw new DelegateNotFoundError(ref.id);
      }
      if (callers.length > 0 && !callers.includes(call.caller)) {
        throw new WaitRefError(`delegate call ${ref.id} belongs to another caller`);
      }
      // A refused call never started and nothing will start it: waiting on it would never end.
      if (call.state === "refused") {
        throw new WaitRefError(
          `delegate call ${ref.id} was refused (it never started): reissue it, mu_delegate_reissue`,
        );
      }
    }
  }
  const umbrella =
    opts.blockersOf === undefined
      ? undefined
      : taskIdFor(db, opts.blockersOf.id, opts.blockersOf.workstream);
  if (opts.blockersOf !== undefined && umbrella === null) {
    throw new TaskNotFoundError(`${opts.blockersOf.workstream}/${opts.blockersOf.id}`);
  }
  const explicit = new Map<string, WaitRef>();
  for (const r of input) explicit.set(refKey(r), r);
  if (opts.wave !== undefined && callers.length === 0) {
    throw new WaitRefError("wait: a wave belongs to callers; none given (no all-caller fallback)");
  }
  if (input.length === 0 && !opts.wave && !opts.blockersOf && callers.length === 0) {
    throw new WaitNothingError();
  }
  // A wave is the callers' (never narrowed by the scope): a name none of their calls carry is a typo.
  if (opts.wave !== undefined) {
    const mine = listDelegatesOf(db, callers, opts.wave.workstream);
    if (!mine.some((c) => c.wave === opts.wave?.name)) {
      throw new WaveNotFoundError(
        opts.wave.name,
        mine.map((c) => c.wave),
      );
    }
  }

  // ─── one active wait per caller; cursors read before any await ────
  const begun = new Map<string, NonNullable<ReturnType<typeof beginWait>>>();
  if (consumes) {
    for (const c of callers) {
      const b = beginWait(db, c);
      if (b !== undefined) begun.set(c, b);
    }
  }
  const cursorsBy = new Map<string, Map<string, CursorToken>>(
    (consumes ? callers : []).map((c) => [c, readCursors(db, c)]),
  );
  /** Whose registration or delegate call holds `key` (else the first caller: where its token is written). */
  const ownerOfKey = new Map<string, string>();
  /** An urgent observation's cursor key -> the ref key it observes. */
  const baseOfCkey = new Map<string, string>();
  const urgentKey = (kind: string, key: string): string => {
    const k = `${kind}|${key}`;
    baseOfCkey.set(k, key);
    return k;
  };
  const ownerOf = (key: string): string | undefined =>
    ownerOfKey.get(baseOfCkey.get(key) ?? key) ?? callers[0];
  const cursorOf = (key: string): CursorToken | undefined => {
    const o = ownerOf(key);
    return o === undefined ? undefined : cursorsBy.get(o)?.get(key);
  };
  const baselineOf = new Map<string, RefBaseline>();
  /** The agent refs some caller has registered (explicit or not). */
  const ownedKeys = new Set<string>();

  // ─── stop signals: abort and the deadline race every await ────────
  const watchAbort = new AbortController();
  let stopReason: "abort" | "deadline" | undefined;
  let stopNow: (why: "abort" | "deadline") => void = () => {};
  const stopP = new Promise<void>((resolve) => {
    stopNow = (why) => {
      stopReason ??= why;
      resolve();
    };
  });
  const onAbort = (): void => {
    watchAbort.abort();
    stopNow("abort");
  };
  if (opts.signal?.aborted) onAbort();
  else opts.signal?.addEventListener("abort", onAbort, { once: true });
  const deadlineTimer =
    deadline === Number.POSITIVE_INFINITY
      ? undefined
      : setTimeout(() => stopNow("deadline"), Math.max(0, deadline - Date.now()));
  type Raced<T> = { lost: false; v: T } | { lost: true };
  const within = <T>(p: Promise<T>): Promise<Raced<T>> =>
    Promise.race([
      p.then((v): Raced<T> => ({ lost: false, v })),
      stopP.then((): Raced<T> => ({ lost: true })),
    ]);

  // A watch settling wakes the loop; a watch that rejected is fatal.
  let fatal: { error: unknown } | undefined;
  let wake: () => void = () => {};
  let woken = new Promise<void>((resolve) => {
    wake = resolve;
  });

  const tracks = new Map<string, AgentTrack>();
  const ownerReadings = new Map<string, AgentReading>();
  /** A wait with no caller owns no durable state: its blockers seen open are remembered for this wait only. */
  const openLocal = new Set<string>();
  const priorTask = new Map<
    string,
    { status: string; owner: string | null; ownerWs: string; ownerId: number }
  >();
  let firstRefresh = true;
  const delivered: Item[] = [];

  /**
   * Provenance: the agents a caller spawned (`agents.spawner`) that own an IN_PROGRESS
   * work task, and those tasks, are owed to that caller. They are registered (durably,
   * with a zero run baseline: a worker the caller spawned has no earlier run it must
   * not be handed), so a worker that dies and is reaped is still seen dead.
   */
  const discoverSpawned = (): void => {
    if (!discover) return;
    const rows = db
      .prepare(
        `SELECT a.name AS agent, a.id AS agentId, a.spawner AS spawner, aws.name AS agentWs,
                t.local_id AS task, tws.name AS taskWs
           FROM agents a
           JOIN workstreams aws ON aws.id = a.workstream_id
           JOIN tasks t ON t.owner_id = a.id AND t.status = 'IN_PROGRESS' AND t.kind = 'work'
           JOIN workstreams tws ON tws.id = t.workstream_id AND tws.torn_down_at IS NULL
          WHERE a.spawner IN (${callers.map(() => "?").join(",")})
          ORDER BY a.id, t.id`,
      )
      .all(...callers) as {
      agent: string;
      agentId: number;
      spawner: string;
      agentWs: string;
      task: string;
      taskWs: string;
    }[];
    const bySpawner = new Map<string, (RegisteredRef & { baseline?: RefBaseline })[]>();
    for (const r of rows) {
      if (!inScope(r.taskWs) && !inScope(r.agentWs)) continue;
      const list = bySpawner.get(r.spawner) ?? [];
      list.push(
        {
          kind: "agent",
          workstream: r.agentWs,
          id: r.agent,
          baseline: { afterRuns: 0, agentId: r.agentId },
        },
        { kind: "task", workstream: r.taskWs, id: r.task },
      );
      bySpawner.set(r.spawner, list);
    }
    for (const [spawner, refs] of bySpawner) {
      const reg = readWaitRegistration(db, spawner);
      const have = new Set((reg?.refs ?? []).map((r) => refKey(r)));
      const fresh = refs.filter((r) => !have.has(refKey(r)));
      if (fresh.length === 0) continue;
      try {
        registerOwnedWait(db, spawner, fresh);
      } catch {
        // A full or refused registration never fails the wait: what it holds is still waited on.
      }
    }
  };

  /** A task as a tick reads it; undefined when it or its workstream is gone. Never throws. */
  const liveTask = (ref: WaitRef) =>
    tryResolveWorkstreamId(db, ref.workstream) === null
      ? undefined
      : getTask(db, ref.id, ref.workstream);

  /** The callers' registered refs, merged (each ref once); records which caller owes which. */
  const ownedRefs = (): WaitRef[] => {
    if (!consumes) return [];
    const out = new Map<string, WaitRef>();
    ownedKeys.clear();
    for (const c of callers) {
      const reg = readWaitRegistration(db, c);
      if (reg === undefined) continue;
      // Owed only while work or a run is owed (src/wait-owed.ts): an agent whose row is gone and
      // which was marked finished is DONE, not dead. Its ref leaves the registration, no event.
      // Into a torn-down or deleted workstream: owed nothing (src/wait-registration.ts deadRefs).
      const dead = deadRefs(db, reg.refs);
      if (dead.length > 0) opts.onDroppedRefs?.(dead);
      const done = [
        ...dead,
        ...reg.refs.filter(
          (r) =>
            !dead.includes(r) && r.kind === "agent" && !agentExists(db, r) && hasDoneMark(db, c, r),
        ),
      ];
      if (done.length > 0) dropOwnedRefs(db, c, done);
      for (const r of reg.refs) {
        if (done.includes(r)) continue;
        if (r.kind !== "delegate" && !inScope(r.workstream)) continue;
        const key = refKey(r);
        ownedKeys.add(key);
        if (!out.has(key)) {
          out.set(key, { ...r });
          ownerOfKey.set(key, c);
        }
        const b = reg.baselines[key];
        if (b !== undefined) {
          const old = baselineOf.get(key);
          if (
            old === undefined ||
            (old.incarnation === b.incarnation && b.afterRuns < old.afterRuns)
          ) {
            baselineOf.set(key, b);
          }
        }
      }
    }
    return [...out.values()];
  };

  const track = (ref: WaitRef, isExplicit: boolean): AgentTrack => {
    const key = refKey(ref);
    let t = tracks.get(key);
    if (t === undefined) {
      t = {
        ref,
        explicit: isExplicit,
        wasBusy: false,
        baseline: ref.afterRuns,
        handle: undefined,
        needWatch: opts.watchAgent !== undefined,
        result: undefined,
        reading: undefined,
        latched: undefined,
        close: undefined,
        muted: false,
        unknownBaseline: false,
      };
      tracks.set(key, t);
    } else if (isExplicit) t.explicit = true;
    return t;
  };

  const setupWatches = async (): Promise<void> => {
    const watch = opts.watchAgent;
    const want = [...tracks.values()].filter((t) => t.needWatch && t.latched === undefined);
    if (watch === undefined || want.length === 0) return;
    for (const t of want) t.needWatch = false;
    const got = await within(
      Promise.all(
        want.map((t) => {
          const key = refKey(t.ref);
          const cur = cursorOf(key);
          const base = baselineOf.get(key);
          return watch(
            { ...t.ref, ...(t.baseline !== undefined ? { afterRuns: t.baseline } : {}) },
            watchAbort.signal,
            {
              owed: ownedKeys.has(key),
              ...(cur !== undefined ? { cursor: cur } : {}),
              ...(base !== undefined ? { baseline: base } : {}),
            },
          );
        }),
      ),
    );
    if (got.lost) return;
    got.v.forEach((h, i) => {
      const t = want[i];
      if (t === undefined) return;
      // No watch could be set up (its control socket did not answer: a lease forward that is
      // down). Watch again once a poll reads the socket answering (advance).
      if (h === undefined) {
        t.watchLost = true;
        return;
      }
      t.handle = h;
      t.reading = h.initial;
      if (t.baseline === undefined && h.afterRuns !== undefined) t.baseline = h.afterRuns;
      if (h.unknownBaseline !== undefined) {
        // Owed with no fence and no cursor: counted from what the watch probed, and
        // that is stored as the fence so a run settling between waits is still caught.
        t.unknownBaseline = true;
        const key = refKey(t.ref);
        const owner = ownerOf(key);
        if (owner !== undefined && consumes)
          fenceAgentRef(db, owner, registered(t.ref), h.unknownBaseline);
      }
      if (h.initial.status === "busy") t.wasBusy = true;
      void h.settled.then(
        (r) => {
          if (t.handle === h) t.result = r;
          wake();
        },
        (error: unknown) => {
          fatal ??= { error };
          wake();
        },
      );
    });
  };

  /** Close notices (src/close-notice.ts): started on a refused settle, awaited briefly on return. */
  const notices = closeNotices(db, callers, watchAbort.signal, () => wake());
  const notifyRefused = (t: AgentTrack, r: AgentReading, close: CloseOutcome | undefined): void =>
    notices.start(t.ref, { ...r, agentId: r.agentId ?? agentRowId(db, t.ref) }, close);

  const settle = (t: AgentTrack, r: AgentReading): void => {
    const consumed =
      !t.explicit &&
      r.runs !== undefined &&
      isConsumed(cursorOf(refKey(t.ref)), { incarnation: r.incarnation ?? "", runs: r.runs });
    const close = consumed ? undefined : closeSettledRun(db, t.ref, r, applyClose);
    const textOk =
      matcher === undefined ||
      (r.lastError === undefined &&
        (r.lastText ?? "").trim() !== "" &&
        matcher.test(r.lastText ?? ""));
    // A refused block is the worker's to fix, whatever this wait was looking for.
    if (!consumed) notifyRefused(t, r, close);
    if (!textOk || consumed) {
      // Not the text asked for, or already delivered: count past this run and wait for the next.
      if (r.runs !== undefined) t.baseline = r.runs;
      t.wasBusy = false;
      t.handle = undefined;
      t.result = undefined;
      t.needWatch = opts.watchAgent !== undefined;
      return;
    }
    t.latched = { kind: "settled", reading: r };
    if (close !== undefined) t.close = close;
  };

  const rearm = (t: AgentTrack, baseline: number): void =>
    rearmLatch(t, baseline, opts.watchAgent !== undefined);

  /** Fold a watch result (`fromWatch`) or a poll reading into the track. */
  const advance = (t: AgentTrack, r: AgentReading, fromWatch: boolean): void => {
    if (t.latched !== undefined) return;
    const pinned = t.ref.incarnation;
    if (pinned !== undefined && r.incarnation !== undefined && r.incarnation !== pinned) {
      throw new AgentRestartedError(t.ref.id, t.ref.workstream, pinned, r.incarnation);
    }
    if (r.blocked === undefined && t.muted) {
      // The dialog is gone: the run goes on; count it again from the same baseline.
      t.muted = false;
      if (opts.watchAgent !== undefined && r.status !== null) {
        t.needWatch = true;
        return;
      }
    }
    if (r.exit !== undefined) {
      // A gate's command ended: its numeric status is the result, never "dead", and no busy-first rule applies.
      t.latched = { kind: "exited", reading: r };
    } else if (r.status === null) {
      t.latched = { kind: "dead", reading: r };
    } else if (r.blocked !== undefined) {
      if (!t.explicit && isConsumed(cursorOf(urgentKey("input", refKey(t.ref))), inputToken(r))) {
        // This dialog was already returned once and is unchanged: not news. The
        // run is polled (a held watch would return blocked again at once).
        t.muted = true;
        t.wasBusy = true;
        t.handle = undefined;
        t.result = undefined;
        t.needWatch = false;
      } else t.latched = { kind: "input", reading: r };
    } else if (
      !fromWatch &&
      matcher !== undefined &&
      r.textSource === "pane" &&
      r.lastText !== undefined &&
      matcher.test(r.lastText)
    ) {
      // A non-pi pane has no settle and no ctl text: what it printed is the only condition
      // there is. It is its OWN event, never `settled` (no run finished), and it never closes a task.
      t.latched = { kind: "pane-text", reading: r };
    } else if (fromWatch) {
      // No settle seen (`unknown`): the watch ends and the agent is polled instead.
      if (r.status === "unknown") t.handle = undefined;
      else settle(t, r);
    } else if (opts.watchAgent !== undefined && rewatchOnPoll(t, r)) {
      // A watch is set up again (see rewatchOnPoll): the settled run is read from it.
    } else if (r.status === "busy") {
      t.wasBusy = true;
    } else if (r.status !== "unknown" && t.wasBusy) {
      settle(t, r);
    }
  };

  /**
   * Keep each owned agent's DONE mark true to what this tick read (src/wait-owed.ts): done when it
   * owes nothing more (a lost pane is then not a death), cleared when it owes again, left alone
   * when the reading cannot say.
   */
  const ownerOfAgent = (t: AgentTrack): { name: string; workstreamName: string } => ({
    name: t.ref.id,
    workstreamName: t.ref.workstream,
  });
  const markOwed = (): void => {
    if (!consumes) return;
    for (const t of tracks.values()) {
      const key = refKey(t.ref);
      const caller = ownerOfKey.get(key);
      const r = t.latched === undefined ? ownerReadings.get(agentKey(ownerOfAgent(t))) : undefined;
      if (caller === undefined || !ownedKeys.has(key) || r === undefined) continue;
      const id = agentRowId(db, t.ref);
      if (id === 0) continue;
      const owed = agentOwed(db, id, r, baselineOf.get(key), t.unknownBaseline, t.wasBusy);
      if (owed !== "unsure")
        setAgentDone(db, t.ref.workstream, t.ref.id, owed === "done", [caller]);
    }
  };

  /** The async half of an iteration: everything that may take time. */
  const refresh = async (): Promise<void> => {
    if (opts.beforePoll) {
      const b = await within(opts.beforePoll());
      if (b.lost) return;
    }
    if (opts.sync !== false) await ambientIngest(db, { quiet: true });
    discoverSpawned();
    const owned = ownedRefs();
    for (const r of explicit.values()) if (r.kind === "agent") track(r, true);
    for (const r of owned) if (r.kind === "agent") track(r, explicit.has(refKey(r)));
    await setupWatches();
    if (stopReason !== undefined) return;
    // Owners of watched in-progress tasks ride on the same batched read.
    const reads = new Map<string, WaitRef>();
    for (const t of tracks.values()) {
      // A settled latch is read too: a later run supersedes it.
      if ((t.latched === undefined && t.handle === undefined) || t.latched?.kind === "settled") {
        reads.set(refKey(t.ref), t.ref);
      }
    }
    // Owned agents are read for real too (a watch's `initial` is only "busy"): what a reading says
    // decides whether the ref is still owed (src/wait-owed.ts).
    if (consumes && opts.readAgents !== undefined) {
      for (const r of owned) {
        const t = r.kind === "agent" ? tracks.get(refKey(r)) : undefined;
        if (t !== undefined && t.latched === undefined) reads.set(refKey(r), r);
      }
    }
    if (observeOwners) {
      const taskRefs = [...explicit.values(), ...owned].filter((r) => r.kind === "task");
      for (const r of taskRefs) {
        const row = liveTask(r);
        const o = row?.status === "IN_PROGRESS" ? getTaskOwner(db, r.id, r.workstream) : undefined;
        if (o !== undefined) {
          const ref: WaitRef = { kind: "agent", workstream: o.workstreamName, id: o.name };
          reads.set(refKey(ref), ref);
        }
      }
    }
    for (const t of tracks.values()) {
      const r = t.result;
      if (r === undefined || t.handle === undefined) continue;
      t.result = undefined;
      t.reading = r;
      advance(t, r, true);
    }
    if (reads.size > 0) {
      if (opts.readAgents === undefined) throw new Error("wait: an agent reader is required");
      const got = await within(opts.readAgents([...reads.values()], watchAbort.signal));
      if (got.lost) return;
      ownerReadings.clear();
      for (const ref of reads.values()) {
        const key = agentKey({ name: ref.id, workstreamName: ref.workstream });
        let r = got.v.get(key);
        if (r === undefined) {
          // A caller's own refs: an agent that is gone is dead, not unknown.
          r =
            watchDeaths && !agentExists(db, ref)
              ? { status: null, reason: "agent is no longer registered" }
              : { status: "unknown", reason: "state reader returned no row" };
        }
        ownerReadings.set(key, r);
        const t = tracks.get(refKey(ref));
        const held = t === undefined ? undefined : supersededBy(t, r);
        if (t !== undefined && held !== undefined) {
          // The owner is another identity, or settled a run past the one latched: the latched run is
          // history. Watch again from the new baseline, so the newer run is read and its CLOSE
          // applied; a polled agent latches it now.
          rearm(t, held);
          if (t.needWatch) continue;
          t.wasBusy = true;
        }
        if (t !== undefined && t.latched === undefined && t.handle === undefined) {
          t.reading = r;
          advance(t, r, false);
        }
      }
    }
    markOwed();
    if (firstRefresh) {
      firstRefresh = false;
      const watched = [...tracks.values()].filter((t) => t.explicit);
      if (
        watched.length > 0 &&
        watched.every((t) => (t.latched?.reading ?? t.reading)?.status === "unknown")
      ) {
        opts.onInitialUnknown?.(watched.map((t) => agentSnapshot(t)));
      }
    }
  };

  // ─── stall detection (the engine's; the policy is handleStall's) ───
  /** Owners whose control socket is broken: first-seen time (the age runs from here). */
  const ctlBrokenSince = new Map<string, number>();
  const stallEmitted = new Set<string>();
  const stallStates = new Map<
    string,
    {
      job?: Promise<void>;
      result?: StallActionResult;
      carry?: { key: string; stall: Item["stall"] };
    }
  >();

  /** How long (ms) the owner has needed attention, or null when it does not. */
  const stallAgeMs = (ownerKey: string, r: AgentReading | undefined): number | null => {
    if (r === undefined || r.status === null) return null;
    const alive = true;
    // A pi owner whose control socket does not answer (pane alive): nothing will report it settling.
    if (alive && (r.ctl === "missing" || r.ctl === "refused")) {
      const first = ctlBrokenSince.get(ownerKey) ?? Date.now();
      ctlBrokenSince.set(ownerKey, first);
      const age = Date.now() - first;
      return age >= stuckAfter(false) ? age : null;
    }
    ctlBrokenSince.delete(ownerKey);
    if (r.status !== "needs_input" || r.since === undefined || r.since === null) return null;
    const viaCtl = r.source === "ctl" && r.ctl === "ok";
    if (viaCtl && r.pending === true) return null;
    const age = Date.now() - r.since;
    return age >= stuckAfter(viaCtl) ? age : null;
  };

  /** Make `item` the stall `stall` describes (also how a handled stall outlives its own effect). */
  const markStall = (item: Item, stall: NonNullable<Item["stall"]>): void => {
    const r = stall.owner;
    item.snap.stuck = true;
    item.snap.stuckWhy =
      r.ctl === "missing" || r.ctl === "refused"
        ? (r.reason ?? `ctl ${r.ctl}`)
        : (r.status ?? "needs_input");
    item.snap.stuckSecs = stall.ageSecs;
    if (r.runs !== undefined) item.snap.ownerRuns = r.runs;
    item.kind = "stall";
    item.ckey = urgentKey("stall", item.key);
    item.token = { incarnation: `stall:${stall.token}`, runs: 1 };
    item.stall = stall;
    // A stall carried past its own effect (a release cleared the owner) still says whose and why.
    item.snap.owner ??= stall.ownerRef.name;
    item.snap.ownerWorkstream ??= stall.ownerRef.workstreamName;
    if (stall.close !== undefined) item.snap.close = stall.close;
  };

  const taskItem = (
    ref: WaitRef,
    want: TaskStatus,
    isTarget: boolean,
    suppressible: boolean,
    order: number,
  ): Item => {
    const row = liveTask(ref);
    // A task deleted mid-wait (or its workstream torn down) reads as OPEN: never reached.
    const status = row?.status ?? "OPEN";
    const ownerRef = row === undefined ? undefined : getTaskOwner(db, ref.id, ref.workstream);
    const ownerName = ownerRef?.name ?? row?.ownerName ?? null;
    const snap: WaitSnapshot = {
      ref,
      status,
      substate: row?.substate ?? DEFAULT_SUBSTATE.OPEN,
      owner: ownerName,
      ...(ownerRef !== undefined ? { ownerWorkstream: ownerRef.workstreamName } : {}),
      fired: false,
      dead: false,
    };
    const item: Item = {
      key: refKey(ref),
      ref,
      snap,
      target: isTarget,
      suppressible,
      order,
      done: false,
      ready: false,
    };
    if (row === undefined) return item;
    // The owner died before the task closed: reaper precedence over closure.
    if (watchDeaths && want === "CLOSED" && status !== "CLOSED") {
      const prior = priorTask.get(item.key);
      const ownerKey = ownerRef === undefined ? undefined : agentKey(ownerRef);
      // The reaper's flip in the ops log (durable): the task's latest status change, after the anchor.
      // A ref a caller registered owes every flip since it was registered (its cursor hides the
      // delivered one); an explicit or blocker ref, those since this wait (or the handle) began.
      const flip =
        status === "OPEN" ? reapFlip(db, ref, ownedKeys.has(item.key) ? 0 : sinceSeq) : undefined;
      const dead =
        (ownerKey !== undefined && ownerReadings.get(ownerKey)?.status === null) ||
        // The reaper flip seen live: IN_PROGRESS -> OPEN while the owner's row (that very row) is
        // gone. A release leaves the owner registered; a replaced agent is another row.
        (prior?.status === "IN_PROGRESS" &&
          status === "OPEN" &&
          prior.owner !== null &&
          prior.ownerId !== 0 &&
          agentRowId(db, { workstream: prior.ownerWs, id: prior.owner }) !== prior.ownerId) ||
        flip !== undefined;
      if (dead) {
        snap.dead = true;
        item.kind = "dead";
        item.done = true;
        item.ckey = urgentKey("dead", item.key);
        const rowId =
          ownerRef !== undefined ? taskOwnerRowId(db, ref) : prior?.ownerId || flip?.ownerId || 0;
        item.token = { incarnation: `dead:${rowId}`, runs: 1 };
        const name = ownerRef?.name ?? prior?.owner ?? flip?.ownerName;
        const workstream =
          ownerRef?.workstreamName ?? (prior?.owner != null ? prior.ownerWs : undefined);
        item.death = {
          rowId,
          ...(name != null ? { name } : {}),
          ...(workstream !== undefined ? { workstream } : {}),
        };
        return item;
      }
    }
    // A work task whose owner needs attention: a stall (exit 7), never a close.
    if (
      stuckEnabled &&
      status === "IN_PROGRESS" &&
      row.kind === "work" &&
      ownerRef !== undefined &&
      want === "CLOSED"
    ) {
      const ownerKey = agentKey(ownerRef);
      const r = ownerReadings.get(ownerKey);
      // An owner whose settled run is still on its way (a watched agent with runs past its
      // baseline) is not stalled: the watch delivers that run, and with it the CLOSE block
      // that may close this very task. A stall must never beat an unprocessed settle.
      const watched = tracks.get(
        refKey({ kind: "agent", workstream: ownerRef.workstreamName, id: ownerRef.name }),
      );
      const settleOwed =
        watched !== undefined &&
        // the settle is in hand but not delivered yet: under --first it goes first, the stall
        // (if the task is still open after its CLOSE block) is the NEXT wait's news. Under --all
        // a settle is no target and is never delivered before the end: its CLOSE is already
        // applied (settle()), so a task it left open (a malformed block) is a stall NOW.
        ((!all && watched.latched?.kind === "settled") ||
          (watched.latched === undefined &&
            (watched.handle !== undefined || watched.needWatch) &&
            r?.runs !== undefined &&
            (watched.baseline ?? 0) < r.runs));
      const age = settleOwed ? null : stallAgeMs(ownerKey, r);
      if (age !== null && r !== undefined) {
        const token = [
          row.workstreamId,
          row.name,
          ownerKey,
          r.incarnation ?? "",
          r.runs ?? "",
          r.since ?? "",
        ].join("|");
        markStall(item, {
          token,
          owner: r,
          ownerRef,
          ageSecs: Math.round(age / 1000),
          // The owner's settled run was refused its CLOSE (a malformed block): say why on the task.
          ...(watched?.close?.applied === false ? { close: watched.close } : {}),
        });
        if (!stallEmitted.has(token)) {
          stallEmitted.add(token);
          // Once per (owner row, pi incarnation, runs) across waits and processes, not per wait. An
          // owner with no run count (no ctl) has only its state's start to tell stalls apart.
          const identity = {
            agentId: r.agentId ?? taskOwnerRowId(db, ref),
            incarnation: `${r.incarnation ?? ""}${r.runs === undefined ? `|since:${r.since ?? ""}` : ""}`,
            runs: r.runs ?? 0,
          };
          logStallOnce(db, ref, identity, () =>
            emitEvent(
              db,
              ref.workstream,
              "agent.stall",
              `agent stalled ${ownerRef.name} owns ${ref.id} for ${Math.round(age / 1000)}s`,
              ownerRef.name,
            ),
          );
        }
        return item;
      }
    }
    if (status !== want) return item;
    if (matcher !== undefined) {
      const rep = db
        .prepare(
          `SELECT n.content FROM task_notes n JOIN tasks t ON t.id = n.task_id
             JOIN workstreams w ON w.id = t.workstream_id
            WHERE w.name = ? AND t.local_id = ? AND n.kind = 'report' ORDER BY n.id DESC LIMIT 1`,
        )
        .get(ref.workstream, ref.id) as { content: string } | undefined;
      if (row.substate !== "done" || rep === undefined || !matcher.test(rep.content)) return item;
    }
    const note = db
      .prepare(
        `SELECT COALESCE(MAX(n.id), 0) AS n FROM task_notes n JOIN tasks t ON t.id = n.task_id
           JOIN workstreams w ON w.id = t.workstream_id WHERE w.name = ? AND t.local_id = ?`,
      )
      .get(ref.workstream, ref.id) as { n: number };
    snap.fired = true;
    item.kind = "closed";
    item.done = true;
    item.token = closedToken(note.n);
    return item;
  };

  const agentItem = (t: AgentTrack, isTarget: boolean, order: number): Item => {
    const l = t.latched;
    const r = l?.reading;
    const key = refKey(t.ref);
    let token: CursorToken | undefined;
    let ckey: string | undefined;
    let death: Item["death"];
    if (l?.kind === "settled" && r?.runs !== undefined) {
      token = { incarnation: r.incarnation ?? "", runs: r.runs };
    } else if (l?.kind === "input" && r !== undefined) {
      ckey = urgentKey("input", key);
      token = inputToken(r);
    } else if (l?.kind === "dead") {
      ckey = urgentKey("dead", key);
      const rowId = agentRowId(db, t.ref);
      token = { incarnation: `dead:${rowId}`, runs: 1 };
      // A reaped row reads 0: the reading may still say which row it was.
      death = { rowId: rowId || (r?.agentId ?? 0), name: t.ref.id, workstream: t.ref.workstream };
    } else if (l?.kind === "exited") {
      // One token per registry row: an owned cursor consumes the result once; a replacement is a new row.
      token = { incarnation: `exited:${agentRowId(db, t.ref)}`, runs: 1 };
    }
    return {
      key,
      ref: t.ref,
      snap: agentSnapshot(t),
      target: isTarget,
      suppressible: !t.explicit,
      order,
      done:
        l?.kind === "settled" ||
        l?.kind === "pane-text" ||
        l?.kind === "dead" ||
        l?.kind === "exited",
      ready: false,
      ...(l !== undefined ? { kind: l.kind } : {}),
      ...(token !== undefined ? { token } : {}),
      ...(ckey !== undefined ? { ckey } : {}),
      ...(death !== undefined ? { death } : {}),
    };
  };

  const OPEN_TOKEN: CursorToken = { incarnation: "open", runs: 1 };
  /** Record (once, durably) that this caller has seen the blocker open. */
  const markOpen = (okey: string): void => {
    if (cursorOf(okey) !== undefined) return;
    const owner = ownerOf(okey);
    if (owner === undefined) return;
    recordCursors(db, owner, [{ key: okey, token: OPEN_TOKEN }]);
    cursorsBy.get(owner)?.set(okey, OPEN_TOKEN);
  };

  /** The synchronous half: the current picture, from the DB and what refresh saw. */
  let tally: VerdictTally | undefined;
  const evaluate = (): Item[] => {
    const items: Item[] = [];
    const byKey = new Map<string, Item>();
    let order = 0;
    const push = (it: Item): void => {
      const prev = byKey.get(it.key);
      if (prev !== undefined) {
        prev.target ||= it.target;
        prev.suppressible &&= it.suppressible;
        return;
      }
      byKey.set(it.key, it);
      items.push(it);
    };
    const owned = ownedRefs();
    const calls = new Map<string, { call: DelegateRow; target: boolean; suppressible: boolean }>();
    const addCall = (
      c: DelegateRow | undefined,
      isTarget: boolean,
      suppressible: boolean,
    ): void => {
      // A refused call is not owed (no run will answer it; the tally still counts it).
      if (c === undefined || c.state === "refused") return;
      const prev = calls.get(c.callId);
      calls.set(c.callId, {
        call: c,
        target: (prev?.target ?? false) || isTarget,
        suppressible: (prev?.suppressible ?? true) && suppressible,
      });
    };
    for (const r of input) {
      if (r.kind === "task") push(taskItem(r, target, true, false, order++));
      else if (r.kind === "agent") push(agentItem(track(r, true), true, order++));
      else addCall(getDelegateCall(db, r.id), true, false);
    }
    if (opts.wave !== undefined) {
      // The caller's own calls of the wave, whatever the scope: a delegate lives in `scratch`
      // unless told otherwise, so `-w <ws>` narrowing it would hide the whole wave.
      const waveCalls = listDelegatesOf(db, callers, opts.wave.workstream, {
        wave: opts.wave.name,
      });
      for (const c of waveCalls) addCall(c, true, !all);
      tally = tallyCalls(waveCalls);
    }
    for (const r of owned) {
      if (r.kind !== "delegate") continue;
      const c = getDelegateCall(db, r.id);
      if (c !== undefined && callInScope(c)) addCall(c, false, true);
    }
    // Every call the callers made is owed its report; the cursor hides the consumed ones.
    if (consumes) {
      for (const c of listDelegatesOf(db, callers)) {
        if (!callInScope(c)) continue;
        addCall(c, false, true);
        ownerOfKey.set(
          refKey({ kind: "delegate", workstream: c.workstream, id: c.callId }),
          c.caller,
        );
      }
    }
    for (const { call, target: isTarget, suppressible } of calls.values()) {
      push(delegateItem(call, isTarget, suppressible, order++));
    }
    if (opts.blockersOf !== undefined && umbrella !== undefined && umbrella !== null) {
      const rows = openBlockers(db, umbrella);
      let open = false;
      let readyBlocker = false;
      for (const b of rows) {
        const ref: WaitRef = { kind: "task", workstream: b.ws, id: b.id };
        const it = taskItem(ref, "CLOSED", true, !all, order++);
        const okey = urgentKey("open", it.key);
        if (!it.done) {
          open = true;
          // Seen open is durable (the caller's cursor), so a close that a `first`
          // did not return is still owed to the next wait.
          if (consumes) markOpen(okey);
          else openLocal.add(it.key);
        } else if (!all && !(consumes ? cursorOf(okey) !== undefined : openLocal.has(it.key))) {
          // First: a blocker that was never seen open is history, not news.
          it.suppressible = true;
          it.token = undefined;
          it.kind = undefined;
        } else if (it.token === undefined || !isConsumed(cursorOf(it.key), it.token)) {
          readyBlocker = true;
        }
        push(it);
      }
      if (!open && !readyBlocker) {
        const ref: WaitRef = { kind: "task", ...opts.blockersOf };
        push({
          key: `blockers:${refKey(ref)}`,
          ref,
          snap: { ref, status: "CLOSED", fired: true, dead: false },
          target: true,
          suppressible: false,
          order: order++,
          kind: "closed",
          done: true,
          ready: false,
          // A state ("nothing is left to wait on"), never news: behind every real event.
          seen: true,
        });
      }
    }
    for (const r of owned) {
      if (r.kind === "task") push(taskItem(r, target, false, true, order++));
    }
    for (const r of owned) {
      if (r.kind === "agent") push(agentItem(track(r, false), false, order++));
    }
    // Remember each task's owner, to tell a reaper flip from a release.
    for (const it of items) {
      if (it.ref.kind !== "task") continue;
      const owner = it.snap.owner ?? null;
      const before = priorTask.get(it.key);
      // Only a tick that SAW an owner records it: after the flip the owner is null.
      const keep = owner === null && it.snap.status === "OPEN" ? before : undefined;
      priorTask.set(it.key, {
        status: it.snap.status ?? "OPEN",
        owner: owner ?? keep?.owner ?? null,
        ownerWs: it.snap.ownerWorkstream ?? keep?.ownerWs ?? it.ref.workstream,
        ownerId: owner === null ? (keep?.ownerId ?? 0) : taskOwnerRowId(db, it.ref),
      });
    }
    const shown: Item[] = [];
    for (const it of items) {
      const consumed = it.token !== undefined && isConsumed(cursorOf(it.ckey ?? it.key), it.token);
      it.seen ||= consumed;
      const hidden = it.suppressible && consumed;
      it.hidden = hidden;
      it.ready = it.kind !== undefined && !hidden;
      const urgent = it.kind === "input" || it.kind === "stall" || it.kind === "dead";
      // An unchanged urgent observation that was returned is not news again, and
      // not "done": the ref is still owed its completion.
      if (hidden && urgent) it.done = false;
      // Consumed owned history is no longer owed: it is not part of the picture.
      if (hidden && !it.target && !urgent) continue;
      shown.push(it);
    }
    return shown;
  };

  /**
   * Hand each stall to `handleStall` (raced: the job runs beside the loop and
   * wakes it), once per stallToken. A stall stays unselectable while its job is
   * out or after a `continue`; `return` (or no handler) makes it the event.
   */
  const driveStalls = (items: Item[]): void => {
    const handler = opts.handleStall;
    // A handler that returned `return` may have removed the very condition (a
    // release leaves the task OPEN and unowned): the stall is still what this
    // wait reports, with the task as it is now.
    for (const st of stallStates.values()) {
      if (st.job !== undefined || st.result?.disposition !== "return" || st.carry === undefined) {
        continue;
      }
      const { key, stall } = st.carry;
      const gone = items.find((i) => i.key === key && i.kind !== "stall");
      if (gone !== undefined && stall !== undefined) {
        markStall(gone, stall);
        gone.hidden = false;
      }
    }
    for (const it of items) {
      if (it.stall === undefined || it.kind !== "stall" || it.hidden === true) continue;
      // `warn`: flagged (`stuck`, the agent.stall event), never returned: the wait goes on.
      if (opts.onStall === "warn") {
        it.ready = false;
        continue;
      }
      const steering = opts.onStall === "steer" || opts.onStall === "release";
      const caller = ownerOf(it.key);
      if (!steering || handler === undefined || caller === undefined) {
        it.ready = true;
        continue;
      }
      const { token, owner, ownerRef } = it.stall;
      // The close notice already answers this run: no steer on top of it; wait for the next run.
      if (opts.onStall === "steer" && notices.answers(ownerRef, owner.runs)) {
        it.ready = false;
        continue;
      }
      let st = stallStates.get(token);
      if (st === undefined) {
        const own = db
          .prepare(
            `SELECT a.id, a.pane_id FROM agents a JOIN workstreams w ON w.id = a.workstream_id
              WHERE a.name = ? AND w.name = ?`,
          )
          .get(ownerRef.name, ownerRef.workstreamName) as
          | { id: number; pane_id: string }
          | undefined;
        const taskId = liveTask(it.ref) && taskIdFor(db, it.ref.id, it.ref.workstream);
        if (own === undefined || taskId === undefined || taskId === null) {
          it.ready = true;
          continue;
        }
        const ctx: WaitStallContext = {
          caller,
          generation: begun.get(caller)?.generation ?? null,
          ref: it.ref,
          taskId,
          revision: taskRevision(db, it.ref.workstream, it.ref.id),
          owner: {
            workstream: ownerRef.workstreamName,
            name: ownerRef.name,
            agentId: owner.agentId ?? own.id,
            paneId: owner.paneId ?? own.pane_id,
            status: owner.status,
            source: owner.source ?? "none",
            since: owner.since ?? null,
            settled: false,
            ...(owner.ctl !== undefined ? { ctl: owner.ctl } : {}),
            ...(owner.incarnation !== undefined ? { incarnation: owner.incarnation } : {}),
            ...(owner.runs !== undefined ? { runs: owner.runs } : {}),
            ...(owner.pending !== undefined ? { pending: owner.pending } : {}),
            ...(owner.reason !== undefined ? { reason: owner.reason } : {}),
          },
          stallToken: token,
          deadlineMs: Number.isFinite(deadline)
            ? Math.max(0, deadline - Date.now())
            : Number.MAX_SAFE_INTEGER,
          signal: watchAbort.signal,
        };
        const fresh: StallState = {};
        const action = opts.onStall === "release" ? "release" : "steer";
        fresh.job = Promise.resolve()
          .then(() => handler(ctx))
          .catch(
            (e: unknown): StallActionResult => ({
              disposition: "return",
              action,
              state: "unknown",
              reason: e instanceof Error ? e.message : String(e),
            }),
          )
          .then((res) => {
            fresh.result = res;
            fresh.job = undefined;
            // The owner's next run counts from the actual send baseline.
            const t = tracks.get(
              refKey({
                kind: "agent",
                ...{ workstream: ownerRef.workstreamName, id: ownerRef.name },
              }),
            );
            if (t !== undefined && res.afterRuns !== undefined) {
              // A latched settle is the run this steer answers (under --all a stall may stand past
              // it): drop it and watch again from the send baseline, or the owner's NEXT run (and its
              // CLOSE) is never read. Any other latch (dead, input, ...) stays.
              if (t.latched?.kind === "settled") rearm(t, res.afterRuns);
              else if (t.latched === undefined) {
                t.baseline = res.afterRuns;
                t.wasBusy = false;
              }
            }
            wake();
          });
        fresh.carry = { key: it.key, stall: it.stall };
        st = fresh;
        stallStates.set(token, st);
      }
      it.ready = st.job === undefined && st.result?.disposition === "return";
    }
  };

  const select = (items: Item[]): { event: Item; fired: Item[] } | undefined => {
    const rank = (a: Item, b: Item): number =>
      // Never-returned events first: an event that was already returned once (an
      // unchanged dialog, a dead pane, a stall) never outranks one that was not.
      Number(a.seen === true) - Number(b.seen === true) ||
      urgency(a.kind) - urgency(b.kind) ||
      // Under --all a failed gate is the firing event, so its code is the exit.
      Number(failedGate(b)) - Number(failedGate(a)) ||
      Number(b.target) - Number(a.target) ||
      a.order - b.order;
    const ready = items.filter((i) => i.ready).sort(rank);
    if (!all) {
      const best = ready[0];
      if (best === undefined) return undefined;
      // One fact, one event: a settled run whose CLOSE block closed task T, and T's own close,
      // are the same news. Delivering either consumes the other, so the second is not owed.
      const same = (a: Item, b: Item): boolean =>
        a.kind === "settled" &&
        a.ref.kind === "agent" &&
        a.snap.close?.applied === true &&
        a.snap.close.task === b.ref.id &&
        a.ref.workstream === b.ref.workstream &&
        b.ref.kind === "task" &&
        b.kind === "closed";
      const companions = ready.filter(
        (i) => i !== best && (same(i, best) || same(best, i) || sameDeath(i, best)),
      );
      return { event: best, fired: [best, ...companions] };
    }
    const urgent = ready.find(
      (i) =>
        i.kind === "input" ||
        i.kind === "stall" ||
        (!i.target && (i.kind === "dead" || i.kind === "delegate")),
    );
    if (urgent !== undefined) {
      const companions = ready.filter((i) => i !== urgent && sameDeath(i, urgent));
      return { event: urgent, fired: [urgent, ...companions] };
    }
    const targets = items.some((i) => i.target) ? items.filter((i) => i.target) : items;
    if (targets.length === 0 || !targets.every((i) => i.done)) return undefined;
    const sorted = [...targets].sort(rank);
    const best = sorted.find((i) => i.ready) ?? sorted[0];
    return best === undefined ? undefined : { event: best, fired: targets.filter((i) => i.done) };
  };

  const build = (
    kind: WaitEventKind,
    items: Item[],
    chosen?: { event: Item; fired: Item[] },
    drained = false,
  ): WaitResult => {
    const e = chosen?.event;
    for (const f of chosen?.fired ?? []) delivered.push(f);
    return {
      event: {
        kind,
        ref: e?.ref ?? null,
        ...(e?.snap.incarnation !== undefined ? { incarnation: e.snap.incarnation } : {}),
        ...(e?.snap.runs !== undefined ? { runs: e.snap.runs } : {}),
      },
      refs: snapshotsOf(items, chosen?.fired ?? []),
      delivered: (chosen?.fired ?? []).filter(isCompletion).map((i) => i.ref),
      timedOut: kind === "heartbeat" && !drained,
      interrupted: kind === "interrupted",
      exit:
        exitFor(kind) !== 0
          ? exitFor(kind)
          : ((chosen?.fired ?? []).find(failedGate)?.snap.exit ?? 0),
      cursor: {},
      ...(e?.stall !== undefined && stallStates.get(e.stall.token)?.result !== undefined
        ? { action: stallStates.get(e.stall.token)?.result as StallActionResult }
        : {}),
      humanNeeds: callers.flatMap((c) => openHumanNeeds(db, c)),
      sinceSeq,
      ...(tally !== undefined ? { tally } : {}),
      ...(drained ? { drained: true } : {}),
    };
  };

  const rethrow = (): void => {
    if (fatal === undefined) return;
    watchAbort.abort();
    throw fatal.error;
  };

  let committed = false;
  let applied = true;
  /**
   * Deliver what `build` handed out, in ONE immediate transaction: re-read the callers' cursors (a
   * wait in another process may have consumed `event` since this one read them), then end each
   * caller's wait and record the tokens. A returned event is consumed (its token written); a
   * completed one also leaves the registration; an urgent one (input, stall, a dead task) stays
   * owed. false = `event` is already consumed: nothing is written, `delivered` is cleared and the
   * caller selects again (with the cursors just read). Without a consuming caller: a no-op.
   */
  const commit = (event?: Item): boolean => {
    if (!consumes) return true;
    const ok = db
      .transaction((): boolean => {
        for (const c of callers) cursorsBy.set(c, readCursors(db, c));
        if (
          // Only news this wait saw as unconsumed can turn stale; the re-read hides it next time.
          event?.token !== undefined &&
          event.ready &&
          event.suppressible &&
          isConsumed(cursorOf(event.ckey ?? event.key), event.token)
        ) {
          return false;
        }
        const completed = delivered.filter(isCompletion);
        // Each delivered ref is consumed by the caller that owns it, guarded by that caller's
        // wait generation.
        for (const c of callers) {
          const mine = completed.filter((i) => ownerOf(i.key) === c);
          const tokens = delivered
            .filter((i) => i.token !== undefined && ownerOf(i.ckey ?? i.key) === c)
            .flatMap((i) =>
              i.token === undefined ? [] : [{ key: i.ckey ?? i.key, token: i.token }],
            );
          const b = begun.get(c);
          if (b !== undefined) {
            const ended = endWait(
              db,
              c,
              b.generation,
              mine.map((i) => ({
                ...registered(i.ref),
                ...(i.kind === "settled" && i.snap.runs !== undefined
                  ? {
                      runs: i.snap.runs,
                      ...(i.snap.incarnation !== undefined
                        ? { incarnation: i.snap.incarnation }
                        : {}),
                    }
                  : {}),
              })),
            );
            if (!ended.applied) {
              applied = false;
              continue;
            }
          }
          recordCursors(db, c, tokens);
        }
        return true;
      })
      .immediate();
    if (ok) committed = true;
    else delivered.length = 0;
    return ok;
  };

  let result: WaitResult | undefined;
  try {
    for (;;) {
      if (stopReason === "abort") {
        // The final snapshot is the state NOW (a DB read), not the last tick's picture:
        // a task that closed while the signal was on its way is reported closed.
        result = build("interrupted", evaluate());
        break;
      }
      if (stopReason === "deadline" || Date.now() >= deadline) {
        const items = evaluate();
        driveStalls(items);
        const chosen = select(items);
        const r = chosen
          ? build(chosen.event.kind ?? "closed", items, chosen)
          : build("heartbeat", items);
        if (!commit(chosen?.event)) continue;
        result = r;
        break;
      }
      await refresh();
      rethrow();
      if (stopReason === "abort") continue;
      const items = evaluate();
      driveStalls(items);
      opts.onPicture?.(items.map((i) => i.snap));
      if (items.length === 0) throw new WaitNothingError();
      const chosen = select(items);
      if (chosen !== undefined) {
        const r = build(chosen.event.kind ?? "closed", items, chosen);
        // Consumed by another process since this wait read its cursors: select again.
        if (!commit(chosen.event)) continue;
        result = r;
        break;
      }
      if (!all && items.every((i) => i.done)) {
        // Everything is done and already delivered: nothing is owed.
        result = build("heartbeat", items, undefined, true);
        break;
      }
      if (stopReason !== undefined) continue;
      // Poll only what a watch does not cover: DB-backed refs, a caller's
      // registration and polled agents. All agents watched: sleep until a
      // settle, the deadline or an abort.
      const polled =
        watchDeaths ||
        observeOwners ||
        items.some((i) => i.ref.kind !== "agent") ||
        [...tracks.values()].some((t) => t.latched === undefined && t.handle === undefined);
      const left = deadline - Date.now();
      const ms = polled ? Math.min(pollMs, left) : left;
      if (ms <= 0) continue;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const sleeper = opts.sleep ?? sleepSeam;
      const slept = !polled
        ? new Promise<void>(() => {})
        : sleeper
          ? sleeper(ms)
          : new Promise<void>((resolve) => {
              timer = setTimeout(resolve, ms);
            });
      await Promise.race([woken, slept, stopP]);
      clearTimeout(timer);
      woken = new Promise<void>((resolve) => {
        wake = resolve;
      });
      rethrow();
    }
  } finally {
    clearTimeout(deadlineTimer);
    opts.signal?.removeEventListener("abort", onAbort);
    await notices.settle(stopReason === undefined);
    watchAbort.abort();
    if (consumes) {
      if (!committed) commit();
      if (result !== undefined) {
        result.cursor = Object.fromEntries(callers.flatMap((c) => [...readCursors(db, c)]));
        if (!applied) result.superseded = true;
      }
    } else if (result !== undefined) result.cursor = {};
    if (opts.sync !== false) await ambientFlush(db, { quiet: true });
  }
  return result as WaitResult;
}

export * from "./wait-types.js";
